import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
  Link as CLink,
} from "@chakra-ui/react";
import { ethers } from "ethers";
import Moralis from "moralis-v1";
import { useMoralis } from "react-moralis";

const DEFAULT_BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD";
const DEFAULT_NATIVE_GAS_RESERVE = 15000000000000000n;
const ACTION_STORE_KEY = "assetExecution.actions";
const BUYBACK_STORE_KEY = "assetExecution.buybackPlans";
const MORALIS_API_KEY =
  (Math.random() < 0.5
    ? process.env.REACT_APP_MORALIS_API_KEY
    : process.env.REACT_APP_MORALIS_API_KEY_2) ||
  process.env.REACT_APP_MORALIS_API_KEY;

const POSITION_CHAINS = [
  { id: 25, moralisChain: "cronos", name: "Cronos", nativeSymbol: "CRO", nativeDecimals: 18 },
  { id: 42161, moralisChain: "arbitrum", name: "Arbitrum", nativeSymbol: "ETH", nativeDecimals: 18 },
];
const NATIVE_TOKEN_SENTINEL = "native";

const ERC20_TRANSFER_ABI = ["function transfer(address to,uint256 amount) returns (bool)"];
const ERC20_TRADE_ABI = [
  "function allowance(address owner,address spender) view returns (uint256)",
  "function approve(address spender,uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
];
const ERC721_TRANSFER_ABI = [
  "function safeTransferFrom(address from,address to,uint256 tokenId)",
];
const ERC1155_TRANSFER_ABI = [
  "function safeTransferFrom(address from,address to,uint256 id,uint256 amount,bytes data)",
];
const V2_ROUTER_ABI = [
  "function getAmountsOut(uint amountIn,address[] calldata path) view returns (uint[] memory amounts)",
  "function swapExactTokensForTokens(uint amountIn,uint amountOutMin,address[] calldata path,address to,uint deadline) returns (uint[] memory amounts)",
];
const V3_SWAP_ROUTER_ABI = [
  "function exactInputSingle(tuple(address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 deadline,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96) params) payable returns (uint256 amountOut)",
];
const QUOTER_V2_ABI = [
  "function quoteExactInputSingle(tuple(address tokenIn,address tokenOut,uint256 amountIn,uint24 fee,uint160 sqrtPriceLimitX96) params) returns (uint256 amountOut,uint160 sqrtPriceX96After,uint32 initializedTicksCrossed,uint256 gasEstimate)",
];

const DASHBOARD_TRADE_CONFIGS = {
  42161: {
    chainName: "Arbitrum",
    dexName: "Uniswap V3",
    routerType: "v3",
    routerAddress: "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45",
    quoterAddress: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
    wrappedNativeToken: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
    stablecoinAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    stablecoinSymbol: "USDC",
    stablecoinDecimals: 6,
    feeTiers: [500, 3000, 10000],
    slippageBps: 100,
  },
  25: {
    chainName: "Cronos",
    dexName: "VVS",
    routerType: "v2",
    routerAddress: "0x145863Eb42Cf62847A6Ca784e6416C1682b1b2Ae",
    wrappedNativeToken: "0x5C7F8A570d578ED84E63fdFA7b1eE72dEae1AE23",
    stablecoinAddress: "0xc21223249CA28397B4B6541dfFaEcC539BfF0c59",
    stablecoinSymbol: "USDC",
    stablecoinDecimals: 6,
    slippageBps: 100,
  },
};

/**
 * AssetType = "native" | "erc20" | "erc721" | "erc1155"
 * WalletAssetPosition, ExecutedAssetAction, BuybackPlan, SellProvider, SellQuote,
 * and AssetExecutionStore follow the prompt contracts. This repo currently uses
 * JSX, so these JSDoc boundaries keep the module drop-in compatible.
 */

function isAddress(value) {
  return typeof value === "string" && ethers.utils.isAddress(value);
}

function isAddressLikeHash(value) {
  return typeof value === "string" && /^0x[a-fA-F0-9]{64}$/.test(value);
}

export function formatAddress(address) {
  if (!address) return "Not connected";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function formatBigIntAmount(balance, decimals = 18) {
  const raw = typeof balance === "bigint" ? balance : BigInt(balance || 0);
  const scale = 10n ** BigInt(decimals);
  const whole = raw / scale;
  const fraction = raw % scale;
  const fractionText = fraction.toString().padStart(decimals, "0").slice(0, 6);
  return `${whole.toString()}${fractionText.replace(/0+$/, "") ? `.${fractionText.replace(/0+$/, "")}` : ""}`;
}

export function formatCurrency(value, currency = "USD") {
  if (typeof value !== "number") return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: value < 1 ? 6 : 2,
  }).format(value);
}

export function formatDateTime(iso) {
  if (!iso) return "-";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

export function getAssetDisplayName(position) {
  return position?.name && position.name !== position.symbol
    ? `${position.name} (${position.symbol})`
    : position?.symbol || "Unknown asset";
}

function getPositionAmount(position) {
  if (position?.balance != null) return position.balance;
  if (position?.amountSold != null) return BigInt(position.amountSold);
  return 0n;
}

export function getNextCalendarYearStart(date) {
  const year = date.getUTCFullYear();
  return new Date(Date.UTC(year + 1, 0, 1)).toISOString();
}

export function isBuybackAvailable(plan, now = new Date()) {
  return plan?.status === "available" || (plan?.status === "locked" && now >= new Date(plan.availableFrom));
}

export function getBuybackPlanStatus(plan, now = new Date()) {
  if (!plan || plan.status === "completed" || plan.status === "cancelled") return plan?.status || "locked";
  return now >= new Date(plan.availableFrom) ? "available" : "locked";
}

export function getNativeTransferAmount(balance, gasReserve = DEFAULT_NATIVE_GAS_RESERVE) {
  const amount = typeof balance === "bigint" ? balance : BigInt(balance || 0);
  const reserve = typeof gasReserve === "bigint" ? gasReserve : BigInt(gasReserve || 0);
  return amount > reserve ? amount - reserve : 0n;
}

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function serializeBigInts(value) {
  return JSON.stringify(value, (_, item) => (typeof item === "bigint" ? `${item.toString()}n` : item));
}

function parseBigInts(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value, (_, item) => {
      if (typeof item === "string" && /^\d+n$/.test(item)) return BigInt(item.slice(0, -1));
      return item;
    });
  } catch {
    return fallback;
  }
}

function createLocalStorageAssetExecutionStore() {
  const list = (key, userId) => {
    const all = parseBigInts(localStorage.getItem(key), {});
    return all[userId] || [];
  };

  const save = (key, userId, row) => {
    const all = parseBigInts(localStorage.getItem(key), {});
    all[userId] = [...(all[userId] || []).filter((item) => item.id !== row.id), row];
    localStorage.setItem(key, serializeBigInts(all));
  };

  return {
    async listActions(userId) {
      return list(ACTION_STORE_KEY, userId);
    },
    async saveAction(userId, action) {
      save(ACTION_STORE_KEY, userId, action);
    },
    async listBuybackPlans(userId) {
      return list(BUYBACK_STORE_KEY, userId);
    },
    async saveBuybackPlan(userId, plan) {
      save(BUYBACK_STORE_KEY, userId, plan);
    },
    async updateBuybackPlan(userId, planId, patch) {
      const plans = list(BUYBACK_STORE_KEY, userId).map((plan) =>
        plan.id === planId ? { ...plan, ...patch, updatedAt: new Date().toISOString() } : plan
      );
      const all = parseBigInts(localStorage.getItem(BUYBACK_STORE_KEY), {});
      all[userId] = plans;
      localStorage.setItem(BUYBACK_STORE_KEY, serializeBigInts(all));
    },
  };
}

function createMoralisAssetExecutionStore(fallbackStore) {
  const listRows = async (className, userId, jsonField) => {
    const Model = Moralis.Object.extend(className);
    const query = new Moralis.Query(Model);
    query.equalTo("userId", String(userId));
    query.descending("updatedAt");
    const rows = await query.find();
    return rows
      .map((row) => parseBigInts(row.get(jsonField), null))
      .filter(Boolean);
  };

  const upsertRow = async (className, userId, idField, id, jsonField, payload) => {
    const Model = Moralis.Object.extend(className);
    const query = new Moralis.Query(Model);
    query.equalTo("userId", String(userId));
    query.equalTo(idField, String(id));
    let row = await query.first();
    if (!row) row = new Model();
    row.set("userId", String(userId));
    row.set(idField, String(id));
    row.set(jsonField, serializeBigInts(payload));
    row.set("updatedAtIso", new Date().toISOString());
    await row.save();
  };

  return {
    async listActions(userId) {
      try {
        return await listRows("AssetExecutionAction", userId, "actionJSON");
      } catch {
        return fallbackStore.listActions(userId);
      }
    },
    async saveAction(userId, action) {
      try {
        await upsertRow("AssetExecutionAction", userId, "actionId", action.id, "actionJSON", action);
      } catch {
        await fallbackStore.saveAction(userId, action);
      }
    },
    async listBuybackPlans(userId) {
      try {
        return await listRows("AssetExecutionBuybackPlan", userId, "planJSON");
      } catch {
        return fallbackStore.listBuybackPlans(userId);
      }
    },
    async saveBuybackPlan(userId, plan) {
      try {
        await upsertRow("AssetExecutionBuybackPlan", userId, "planId", plan.id, "planJSON", plan);
      } catch {
        await fallbackStore.saveBuybackPlan(userId, plan);
      }
    },
    async updateBuybackPlan(userId, planId, patch) {
      try {
        const plans = await listRows("AssetExecutionBuybackPlan", userId, "planJSON");
        const existing = plans.find((plan) => plan.id === planId);
        if (!existing) throw new Error("Buyback plan not found.");
        const next = { ...existing, ...patch, updatedAt: new Date().toISOString() };
        await upsertRow("AssetExecutionBuybackPlan", userId, "planId", planId, "planJSON", next);
      } catch {
        await fallbackStore.updateBuybackPlan(userId, planId, patch);
      }
    },
  };
}

function normalizeAddress(address) {
  return isAddress(address) ? ethers.utils.getAddress(address) : "";
}

function readStoredWallets(account) {
  const wallets = [];
  if (isAddress(account)) wallets.push(normalizeAddress(account));

  try {
    const stored = JSON.parse(localStorage.getItem("walletsStorage") || "[]");
    if (Array.isArray(stored)) {
      stored.forEach((wallet) => {
        const normalized = normalizeAddress(wallet);
        if (normalized) wallets.push(normalized);
      });
    }
  } catch {
    // Ignore broken legacy storage and fall back to connected wallet.
  }

  return Array.from(new Set(wallets.map((wallet) => wallet.toLowerCase()))).map((wallet) =>
    ethers.utils.getAddress(wallet)
  );
}

async function fetchMoralisJson(url) {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
      "X-API-Key": MORALIS_API_KEY,
    },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Moralis ${response.status}: ${text}`);
  }
  return response.json();
}

function parseRawBalance(raw) {
  try {
    return BigInt(String(raw || "0"));
  } catch {
    return 0n;
  }
}

function getTokenUsdValue(token) {
  if (token.usd_value != null) return Number(token.usd_value) || 0;
  if (token.balance_formatted != null && token.usd_price != null) {
    return (Number(token.balance_formatted) || 0) * (Number(token.usd_price) || 0);
  }
  return undefined;
}

function mapMoralisTokenToPosition(walletAddress, chain, token) {
  const contractAddress = normalizeAddress(token.token_address || token.address);
  const decimals = Number(token.decimals ?? 18);
  const balance = parseRawBalance(token.balance);
  const symbol = token.symbol || "TOKEN";
  return {
    id: `${walletAddress}-${chain.id}-${contractAddress || symbol}`,
    chainId: chain.id,
    chainName: chain.name,
    walletAddress,
    assetType: "erc20",
    contractAddress,
    symbol,
    name: token.name || symbol,
    imageUrl: token.logo || undefined,
    balance,
    decimals,
    estimatedValueUsd: getTokenUsdValue(token),
    canSell: Boolean(balance > 0n && contractAddress && getDashboardTradeConfig(chain.id)),
    canBurn: Boolean(balance > 0n && contractAddress),
    canBuyBack: Boolean(contractAddress && getDashboardTradeConfig(chain.id)),
    sellProviderIds: getDashboardTradeConfig(chain.id) ? ["dashboard-direct-router"] : [],
    lastUpdatedAt: new Date().toISOString(),
  };
}

function parseNftMetadata(nft) {
  const raw = nft.normalized_metadata || nft.metadata;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function mapMoralisNftToPosition(walletAddress, chain, nft) {
  const contractAddress = normalizeAddress(nft.token_address || nft.address);
  const metadata = parseNftMetadata(nft);
  const tokenId = String(nft.token_id ?? nft.tokenId ?? "");
  const amount = parseRawBalance(nft.amount || nft.balance || "1");
  const contractType = String(nft.contract_type || nft.token_type || "ERC721").toLowerCase();
  const symbol = nft.symbol || metadata.symbol || "NFT";
  const name = metadata.name || nft.name || `${symbol}${tokenId ? ` #${tokenId}` : ""}`;
  return {
    id: `${walletAddress}-${chain.id}-${contractAddress}-${tokenId}`,
    chainId: chain.id,
    chainName: chain.name,
    walletAddress,
    assetType: contractType.includes("1155") ? "erc1155" : "erc721",
    contractAddress,
    tokenId,
    symbol,
    name,
    imageUrl: metadata.image || metadata.image_url || nft.media?.media_collection?.high?.url || undefined,
    balance: amount > 0n ? amount : 1n,
    decimals: 0,
    estimatedValueUsd: undefined,
    marketplaceUrl: getNftMarketplaceUrl(chain.id, walletAddress, contractAddress, tokenId),
    canSell: false,
    canBurn: Boolean(contractAddress && tokenId),
    canBuyBack: false,
    sellProviderIds: [],
    lastUpdatedAt: new Date().toISOString(),
  };
}

function getNftMarketplaceUrl(chainId, walletAddress, contractAddress, tokenId) {
  if (!isAddress(walletAddress) || !isAddress(contractAddress)) return undefined;
  if (chainId === 25) {
    return `https://app.ebisusbay.com/account/${walletAddress}?collection=${contractAddress.toLowerCase()}`;
  }
  if (chainId === 42161 && tokenId) {
    return `https://opensea.io/assets/arbitrum/${contractAddress.toLowerCase()}/${tokenId}`;
  }
  return undefined;
}

async function fetchNativePosition(walletAddress, chain) {
  if (!window.ethereum || !isAddress(walletAddress)) return null;
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const network = await provider.getNetwork();
  if (network.chainId !== chain.id) return null;
  const balance = BigInt((await provider.getBalance(walletAddress)).toString());
  return {
    id: `${walletAddress}-${chain.id}-${NATIVE_TOKEN_SENTINEL}`,
    chainId: chain.id,
    chainName: chain.name,
    walletAddress,
    assetType: "native",
    symbol: chain.nativeSymbol,
    name: chain.nativeSymbol,
    balance,
    decimals: chain.nativeDecimals,
    estimatedValueUsd: undefined,
    canSell: false,
    canBurn: balance > DEFAULT_NATIVE_GAS_RESERVE,
    canBuyBack: false,
    sellProviderIds: [],
    lastUpdatedAt: new Date().toISOString(),
  };
}

async function fetchWalletPositionsFromMoralis(account) {
  if (!MORALIS_API_KEY) throw new Error("Missing REACT_APP_MORALIS_API_KEY.");
  const wallets = readStoredWallets(account);
  if (!wallets.length) throw new Error("No connected or saved wallets found.");

  const perWalletChain = await Promise.all(
    wallets.flatMap((walletAddress) =>
      POSITION_CHAINS.map(async (chain) => {
        const tokenUrl = `https://deep-index.moralis.io/api/v2.2/wallets/${walletAddress}/tokens?chain=${chain.moralisChain}&exclude_spam=true&limit=100`;
        const nftUrl = `https://deep-index.moralis.io/api/v2.2/${walletAddress}/nft?chain=${chain.moralisChain}&format=decimal&normalizeMetadata=true&limit=100`;
        const [data, nftData] = await Promise.all([
          fetchMoralisJson(tokenUrl),
          fetchMoralisJson(nftUrl).catch(() => ({ result: [] })),
        ]);
        const tokens = Array.isArray(data?.result) ? data.result : Array.isArray(data) ? data : [];
        const nfts = Array.isArray(nftData?.result) ? nftData.result : Array.isArray(nftData) ? nftData : [];
        const tokenPositions = tokens
          .map((token) => mapMoralisTokenToPosition(walletAddress, chain, token))
          .filter((position) => position.balance > 0n);
        const nftPositions = nfts
          .map((nft) => mapMoralisNftToPosition(walletAddress, chain, nft))
          .filter((position) => position.balance > 0n);
        const nativePosition = await fetchNativePosition(walletAddress, chain).catch(() => null);
        return nativePosition ? [nativePosition, ...tokenPositions, ...nftPositions] : [...tokenPositions, ...nftPositions];
      })
    )
  );

  return perWalletChain.flat();
}

const mockSellProvider = {
  id: "mock-provider",
  name: "Mock Provider",
  supports(position) {
    return position.assetType === "native" || position.assetType === "erc20";
  },
  async getSellQuote(request) {
    return {
      providerId: "mock-provider",
      providerName: "Mock Provider",
      inputAmount: request.amount,
      estimatedOutputAmount: request.amount / 100n,
      outputSymbol: "USDC",
      priceImpact: 0.25,
      feeAmount: request.amount / 1000n,
      requiresApproval: request.assetType === "erc20",
      approvalTarget: "0x0000000000000000000000000000000000000001",
      expiresAt: new Date(Date.now() + 300000).toISOString(),
      nonExecutableReason: "Mock provider is quote-only. Plug a real DEX or marketplace provider into useSellProviders.",
    };
  },
  async buildSellTx() {
    throw new Error("Mock provider cannot build executable sell transactions.");
  },
};

function getDashboardTradeConfig(chainId) {
  return DASHBOARD_TRADE_CONFIGS[chainId] || null;
}

function applySlippage(amountOut, slippageBps) {
  const amount = ethers.BigNumber.from(amountOut || 0);
  return amount.mul(10000 - slippageBps).div(10000);
}

async function getErc20Decimals(provider, tokenAddress, fallback = 18) {
  try {
    const token = new ethers.Contract(tokenAddress, ERC20_TRADE_ABI, provider);
    return Number(await token.decimals());
  } catch {
    return fallback;
  }
}

async function ensureTokenAllowance(signer, tokenAddress, owner, spender, amountIn) {
  const token = new ethers.Contract(tokenAddress, ERC20_TRADE_ABI, signer);
  const allowance = await token.allowance(owner, spender);
  if (allowance.gte(amountIn)) return;
  const tx = await token.approve(spender, ethers.constants.MaxUint256);
  await tx.wait();
}

async function quoteDashboardSell(position, amountIn) {
  const config = getDashboardTradeConfig(position.chainId);
  if (!config) throw new Error(`No dashboard sell config for chain ${position.chainId}.`);
  if (!isAddress(position.contractAddress)) throw new Error("ERC-20 contract address is required.");
  if (!window.ethereum) throw new Error("Wallet read adapter not connected.");

  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const path = [position.contractAddress, config.wrappedNativeToken, config.stablecoinAddress];

  if (config.routerType === "v2") {
    const router = new ethers.Contract(config.routerAddress, V2_ROUTER_ABI, provider);
    const amounts = await router.getAmountsOut(amountIn, path);
    const quoted = amounts[amounts.length - 1];
    return {
      quoted,
      minOut: applySlippage(quoted, config.slippageBps),
      path,
      config,
    };
  }

  const quoter = new ethers.Contract(config.quoterAddress, QUOTER_V2_ABI, provider);
  const tokenIn = ethers.utils.getAddress(position.contractAddress);
  const tokenOut = ethers.utils.getAddress(config.stablecoinAddress);
  let lastError;

  for (const fee of config.feeTiers) {
    try {
      const result = await quoter.callStatic.quoteExactInputSingle([
        tokenIn,
        tokenOut,
        amountIn,
        fee,
        0,
      ]);
      const quoted = result?.amountOut || result?.[0];
      if (quoted && quoted.gt(0)) {
        return {
          quoted,
          minOut: applySlippage(quoted, config.slippageBps),
          fee,
          path: [position.contractAddress, config.stablecoinAddress],
          config,
        };
      }
    } catch (err) {
      lastError = err;
    }
  }

  throw new Error(lastError?.message || "No executable Uniswap V3 quote found for this token.");
}

const dashboardDirectSellProvider = {
  id: "dashboard-direct-router",
  name: "Dashboard Direct Router",
  supports(position) {
    return Boolean(
      position.assetType === "erc20" &&
      isAddress(position.contractAddress) &&
      getDashboardTradeConfig(position.chainId)
    );
  },
  async getSellQuote(request) {
    const quote = await quoteDashboardSell(
      {
        chainId: request.chainId,
        contractAddress: request.contractAddress,
      },
      ethers.BigNumber.from(request.amount.toString())
    );

    return {
      providerId: "dashboard-direct-router",
      providerName: "Dashboard Direct Router",
      inputAmount: request.amount,
      estimatedOutputAmount: BigInt(quote.quoted.toString()),
      outputSymbol: quote.config.stablecoinSymbol,
      priceImpact: undefined,
      feeAmount: undefined,
      requiresApproval: true,
      approvalTarget: quote.config.routerAddress,
      expiresAt: new Date(Date.now() + 120000).toISOString(),
      routerQuote: {
        minOut: quote.minOut.toString(),
        fee: quote.fee,
        path: quote.path,
        routerType: quote.config.routerType,
      },
    };
  },
  async executeSell(position, quote, account) {
    const config = getDashboardTradeConfig(position.chainId);
    if (!config) throw new Error(`No dashboard sell config for chain ${position.chainId}.`);
    if (!isAddress(position.contractAddress)) throw new Error("ERC-20 contract address is required.");
    const signer = await validateWalletContext(position, account);
    const userAddress = await signer.getAddress();
    const amountIn = ethers.BigNumber.from(position.balance.toString());
    const freshQuote = await quoteDashboardSell(position, amountIn);
    const deadline = Math.floor(Date.now() / 1000) + 60 * 40;

    await ensureTokenAllowance(
      signer,
      position.contractAddress,
      userAddress,
      config.routerAddress,
      amountIn
    );

    let tx;
    if (config.routerType === "v2") {
      const router = new ethers.Contract(config.routerAddress, V2_ROUTER_ABI, signer);
      tx = await router.swapExactTokensForTokens(
        amountIn,
        freshQuote.minOut,
        freshQuote.path,
        userAddress,
        deadline,
        { gasLimit: 400000 }
      );
    } else {
      const router = new ethers.Contract(config.routerAddress, V3_SWAP_ROUTER_ABI, signer);
      tx = await router.exactInputSingle(
        {
          tokenIn: ethers.utils.getAddress(position.contractAddress),
          tokenOut: ethers.utils.getAddress(config.stablecoinAddress),
          fee: freshQuote.fee,
          recipient: userAddress,
          deadline,
          amountIn,
          amountOutMinimum: freshQuote.minOut,
          sqrtPriceLimitX96: 0,
        },
        { gasLimit: 500000 }
      );
    }

    const receipt = await tx.wait();
    if (receipt.status === 0) throw new Error(`Sell transaction reverted: ${tx.hash}`);
    return { txHash: tx.hash };
  },
};

function buildTransferAllTx(position, targetAddress, gasReserve = DEFAULT_NATIVE_GAS_RESERVE) {
  if (!isAddress(targetAddress)) throw new Error("Target address is invalid.");
  if (position.balance <= 0n) throw new Error("Balance must be greater than zero.");

  if (position.assetType === "native") {
    const value = getNativeTransferAmount(position.balance, gasReserve);
    if (value <= 0n) throw new Error("Native balance is below the configured gas reserve.");
    return { to: targetAddress, data: "0x", value };
  }

  if (!isAddress(position.contractAddress)) {
    throw new Error("Contract address is required for token transfers.");
  }

  if (position.assetType === "erc20") {
    const iface = new ethers.utils.Interface(ERC20_TRANSFER_ABI);
    return {
      to: position.contractAddress,
      data: iface.encodeFunctionData("transfer", [targetAddress, position.balance.toString()]),
      value: 0n,
    };
  }

  if (position.assetType === "erc721") {
    if (!position.tokenId) throw new Error("ERC-721 tokenId is required.");
    const iface = new ethers.utils.Interface(ERC721_TRANSFER_ABI);
    return {
      to: position.contractAddress,
      data: iface.encodeFunctionData("safeTransferFrom", [
        position.walletAddress,
        targetAddress,
        position.tokenId,
      ]),
      value: 0n,
    };
  }

  if (position.assetType === "erc1155") {
    if (!position.tokenId) throw new Error("ERC-1155 tokenId is required.");
    const iface = new ethers.utils.Interface(ERC1155_TRANSFER_ABI);
    return {
      to: position.contractAddress,
      data: iface.encodeFunctionData("safeTransferFrom", [
        position.walletAddress,
        targetAddress,
        position.tokenId,
        position.balance.toString(),
        "0x",
      ]),
      value: 0n,
    };
  }

  throw new Error("Unsupported asset type.");
}

function useAssetExecutionStore() {
  return useMemo(() => {
    const fallback = createLocalStorageAssetExecutionStore();
    return createMoralisAssetExecutionStore(fallback);
  }, []);
}

function useSellProviders() {
  return useMemo(() => [dashboardDirectSellProvider, mockSellProvider], []);
}

function useWalletPositions(account) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const realPositions = await fetchWalletPositionsFromMoralis(account);
      setPositions(realPositions);
    } catch (err) {
      setPositions([]);
      setError(err?.message || "No data available.");
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { positions, loading, error, refresh };
}

function useBuybackPlans(store, userId) {
  const [plans, setPlans] = useState([]);

  const refresh = useCallback(async () => {
    if (!userId) {
      setPlans([]);
      return;
    }
    const rows = await store.listBuybackPlans(userId);
    setPlans(rows.map((plan) => ({ ...plan, status: getBuybackPlanStatus(plan) })));
  }, [store, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { plans, refresh };
}

async function validateWalletContext(position, account) {
  if (!isAddress(account)) throw new Error("Wallet is not connected.");
  if (account.toLowerCase() !== position.walletAddress.toLowerCase()) {
    throw new Error("Connected wallet does not match the selected row.");
  }
  if (!window.ethereum) throw new Error("Wallet write adapter not connected.");

  const web3 = new ethers.providers.Web3Provider(window.ethereum);
  const network = await web3.getNetwork();
  if (network.chainId !== position.chainId) {
    throw new Error(`Switch wallet to ${position.chainName || `chain ${position.chainId}`} before signing.`);
  }
  return web3.getSigner();
}

async function sendBuiltTransaction(position, account, builtTx) {
  const signer = await validateWalletContext(position, account);
  const tx = await signer.sendTransaction({
    to: builtTx.to,
    data: builtTx.data,
    value: ethers.BigNumber.from(builtTx.value.toString()),
  });
  return tx.wait ? tx.wait().then(() => tx.hash) : tx.hash;
}

function createBuybackPlanFromSell(position, sellAction, providerId) {
  const now = new Date(sellAction.executedAt);
  return {
    id: makeId("buyback"),
    originalSellActionId: sellAction.id,
    chainId: position.chainId,
    chainName: position.chainName,
    walletAddress: position.walletAddress,
    assetType: position.assetType,
    contractAddress: position.contractAddress,
    tokenId: position.tokenId,
    symbol: position.symbol,
    amountSold: sellAction.amount,
    soldAt: sellAction.executedAt,
    availableFrom: getNextCalendarYearStart(now),
    providerId,
    status: "locked",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function LoadingState() {
  return (
    <HStack py={8} justify="center" color="whiteAlpha.800">
      <Spinner />
      <Text>Loading wallet assets...</Text>
    </HStack>
  );
}

function ErrorState({ message }) {
  return (
    <Box borderWidth="1px" borderColor="orange.300" bg="orange.900" color="orange.50" p={4} borderRadius="md">
      {message}
    </Box>
  );
}

function EmptyState({ children }) {
  return (
    <Box borderWidth="1px" borderStyle="dashed" borderColor="whiteAlpha.300" p={8} textAlign="center" borderRadius="md" bg="whiteAlpha.50">
      <Text color="whiteAlpha.700">{children}</Text>
    </Box>
  );
}

function ProviderSelect({ providers, value, onChange, position }) {
  const supported = providers.filter((provider) => provider.supports(position));
  return (
    <FormControl>
      <FormLabel>Provider</FormLabel>
      <Select value={value} onChange={(event) => onChange(event.target.value)}>
        {supported.map((provider) => (
          <option key={provider.id} value={provider.id}>
            {provider.name}
          </option>
        ))}
      </Select>
    </FormControl>
  );
}

function ConfirmActionSummary({ position, actionType, targetAddress, provider, quote, writeStatus }) {
  const amount = getPositionAmount(position);
  return (
    <VStack align="stretch" spacing={3} borderWidth="1px" borderColor="gray.200" p={4} borderRadius="md">
      <HStack justify="space-between">
        <Text color="gray.500">Action</Text>
        <Badge colorScheme={actionType === "sell" ? "purple" : actionType === "buyBack" ? "green" : actionType === "send" ? "cyan" : "red"}>
          {actionType === "burn" ? "Burn all" : actionType === "send" ? "Send all" : actionType}
        </Badge>
      </HStack>
      <HStack justify="space-between">
        <Text color="gray.500">Wallet</Text>
        <Text>{formatAddress(position.walletAddress)}</Text>
      </HStack>
      <HStack justify="space-between">
        <Text color="gray.500">Chain</Text>
        <Text>{position.chainName || position.chainId}</Text>
      </HStack>
      <HStack justify="space-between">
        <Text color="gray.500">Asset</Text>
        <Text>{getAssetDisplayName(position)}</Text>
      </HStack>
      <HStack justify="space-between">
        <Text color="gray.500">Amount</Text>
        <Text>{formatBigIntAmount(amount, position.decimals)} {position.symbol}</Text>
      </HStack>
      {targetAddress ? (
        <HStack justify="space-between">
          <Text color="gray.500">Target</Text>
          <Text>{formatAddress(targetAddress)}</Text>
        </HStack>
      ) : null}
      {provider ? (
        <HStack justify="space-between">
          <Text color="gray.500">Provider</Text>
          <Text>{provider.name}</Text>
        </HStack>
      ) : null}
      {quote ? (
        <Box bg="gray.50" p={3} borderRadius="md">
          <Text fontSize="sm">Estimated output: {formatBigIntAmount(quote.estimatedOutputAmount, 18)} {quote.outputSymbol}</Text>
          <Text fontSize="sm">Expires: {formatDateTime(quote.expiresAt)}</Text>
          {quote.nonExecutableReason ? <Text fontSize="sm" color="orange.600">{quote.nonExecutableReason}</Text> : null}
        </Box>
      ) : null}
      {writeStatus ? (
        <Box bg={writeStatus.ok ? "green.50" : "orange.50"} color={writeStatus.ok ? "green.800" : "orange.800"} p={3} borderRadius="md">
          {writeStatus.message}
        </Box>
      ) : null}
    </VStack>
  );
}

function ExecutionDrawer({
  isOpen,
  onClose,
  action,
  position,
  batchPositions = [],
  plan,
  providers,
  account,
  store,
  userId,
  onChanged,
}) {
  const toast = useToast();
  const [state, setState] = useState({ status: "idle" });
  const [providerId, setProviderId] = useState("mock-provider");
  const [quote, setQuote] = useState(null);
  const [targetMode, setTargetMode] = useState("burn");
  const [targetAddress, setTargetAddress] = useState(DEFAULT_BURN_ADDRESS);
  const [confirmed, setConfirmed] = useState(false);

  const provider = providers.find((item) => item.id === providerId) || providers[0];
  const selectedPosition = position || plan;
  const executionPositions = batchPositions.length ? batchPositions : selectedPosition ? [selectedPosition] : [];
  const primaryPosition = executionPositions[0] || selectedPosition;
  const hasWalletAdapter = typeof window !== "undefined" && Boolean(window.ethereum);
  const walletMatches =
    primaryPosition && isAddress(account)
      ? executionPositions.every((item) => account.toLowerCase() === item.walletAddress.toLowerCase())
      : false;
  const writeStatus = !hasWalletAdapter
    ? { ok: false, message: "Wallet write adapter not connected." }
    : !walletMatches
      ? { ok: false, message: "Connected wallet does not match this row." }
      : { ok: true, message: "Wallet write adapter connected. Chain is checked before signing." };
  const isSell = action === "sell";
  const isBuyBack = action === "buyBack";
  const isTransferAction = action === "burn" || action === "send";
  const canExecute =
    confirmed &&
    primaryPosition &&
    writeStatus.ok &&
    state.status !== "executing" &&
    (!isSell || batchPositions.length || (quote && !quote.nonExecutableReason)) &&
    (!isBuyBack || (quote && provider?.buildBuybackTx));

  useEffect(() => {
    if (!isOpen) return;
    setState({ status: "idle" });
    setQuote(null);
    setConfirmed(false);
    setTargetMode("burn");
    setTargetAddress(action === "send" ? "" : DEFAULT_BURN_ADDRESS);
    if (providers[0]) setProviderId(providers[0].id);
  }, [action, isOpen, providers]);

  useEffect(() => {
    if (action === "burn") setTargetAddress(DEFAULT_BURN_ADDRESS);
  }, [action, targetMode]);

  const loadQuote = useCallback(async () => {
    if (!provider || !primaryPosition || batchPositions.length) return;
    setState({ status: "loadingQuote" });
    try {
      const nextQuote = isBuyBack && provider.getBuybackQuote
        ? await provider.getBuybackQuote(plan)
        : await provider.getSellQuote({
            chainId: primaryPosition.chainId,
            walletAddress: primaryPosition.walletAddress,
            assetType: primaryPosition.assetType,
            contractAddress: primaryPosition.contractAddress,
            tokenId: primaryPosition.tokenId,
            amount: getPositionAmount(primaryPosition),
            fromSymbol: primaryPosition.symbol,
          });
      setQuote(nextQuote);
      setState(nextQuote.requiresApproval ? { status: "approvalRequired" } : { status: "quoteReady" });
    } catch (err) {
      setState({ status: "error", message: err?.message || "Could not load quote." });
    }
  }, [batchPositions.length, isBuyBack, plan, primaryPosition, provider]);

  useEffect(() => {
    if (isOpen && (isSell || isBuyBack)) loadQuote();
  }, [isOpen, isSell, isBuyBack, loadQuote]);

  const execute = async () => {
    if (!primaryPosition) return;
    setState({ status: "executing" });
    try {
      let txHash;
      if (isTransferAction) {
        for (const item of executionPositions) {
          const builtTx = buildTransferAllTx(item, action === "burn" ? DEFAULT_BURN_ADDRESS : targetAddress);
          txHash = await sendBuiltTransaction(item, account, builtTx);
        }
      } else if (action === "sell") {
        for (const item of executionPositions) {
        if (!provider.supports(item)) throw new Error(`Provider does not support ${item.symbol}.`);
        const itemQuote = batchPositions.length
          ? await provider.getSellQuote({
              chainId: item.chainId,
              walletAddress: item.walletAddress,
              assetType: item.assetType,
              contractAddress: item.contractAddress,
              tokenId: item.tokenId,
              amount: getPositionAmount(item),
              fromSymbol: item.symbol,
            })
          : quote;
        if (provider.executeSell) {
          const result = await provider.executeSell(item, itemQuote, account);
          txHash = result?.txHash || result;
        } else {
          const builtTx = await provider.buildSellTx(item, itemQuote);
          txHash = await sendBuiltTransaction(item, account, builtTx);
        }
        if (!txHash || !isAddressLikeHash(txHash)) {
          throw new Error("Sell execution did not return a valid transaction hash.");
        }
        const executedAt = new Date().toISOString();
        const sellAction = {
          id: makeId("action"),
          type: "sell",
          chainId: item.chainId,
          chainName: item.chainName,
          walletAddress: item.walletAddress,
          assetType: item.assetType,
          contractAddress: item.contractAddress,
          tokenId: item.tokenId,
          symbol: item.symbol,
          amount: item.balance.toString(),
          providerId: provider.id,
          txHash,
          executedAt,
        };
        const buybackPlan = createBuybackPlanFromSell(item, sellAction, provider.id);
        await store.saveAction(userId, { ...sellAction, buybackPlanId: buybackPlan.id });
        await store.saveBuybackPlan(userId, buybackPlan);
        }
      } else if (action === "buyBack") {
        const builtTx = await provider.buildBuybackTx(plan, quote);
        txHash = await sendBuiltTransaction(plan, account, builtTx);
        await store.updateBuybackPlan(userId, plan.id, {
          status: "completed",
          buybackTxHash: txHash,
        });
      }
      setState({ status: "success", txHash });
      toast({ status: "success", title: "Transaction completed", description: txHash });
      await onChanged();
    } catch (err) {
      setState({ status: "error", message: err?.message || "Execution failed." });
    }
  };

  if (!primaryPosition) return null;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{action === "burn" ? "Burn asset" : action === "send" ? "Send asset" : action === "sell" ? "Sell all" : "Buy back"}</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={5}>
            {action === "burn" ? (
              <FormControl>
                <FormLabel>Burn address</FormLabel>
                <Input value={DEFAULT_BURN_ADDRESS} isReadOnly />
              </FormControl>
            ) : action === "send" ? (
              <>
                <FormControl>
                  <FormLabel>Target address</FormLabel>
                  <Input
                    value={targetAddress}
                    isReadOnly={false}
                    onChange={(event) => setTargetAddress(event.target.value)}
                    placeholder="0x..."
                  />
                </FormControl>
              </>
            ) : (
              <ProviderSelect providers={providers} value={providerId} onChange={setProviderId} position={primaryPosition} />
            )}

            <ConfirmActionSummary
              position={primaryPosition}
              actionType={action}
              targetAddress={isTransferAction ? targetAddress : undefined}
              provider={isTransferAction ? undefined : provider}
              quote={quote}
              writeStatus={writeStatus}
            />
            {executionPositions.length > 1 ? (
              <Box bg="whiteAlpha.100" p={3} borderRadius="md">
                <Text fontSize="sm" color="whiteAlpha.800">
                  Batch includes {executionPositions.length} rows. Your wallet will ask you to validate each transaction separately.
                </Text>
              </Box>
            ) : null}

            {state.status === "approvalRequired" ? (
              <Box bg="orange.50" color="orange.800" p={3} borderRadius="md">
                Approval is required before this provider can execute. Your wallet will ask for approval before the swap transaction when needed.
              </Box>
            ) : null}

            {state.status === "error" ? <ErrorState message={state.message} /> : null}
            {state.status === "success" ? (
              <Box bg="green.50" color="green.800" p={3} borderRadius="md">
                Success: {state.txHash}
              </Box>
            ) : null}

            <Checkbox isChecked={confirmed} onChange={(event) => setConfirmed(event.target.checked)}>
              I understand this signs exactly one transaction for the selected row.
            </Checkbox>
          </VStack>
        </DrawerBody>
        <DrawerFooter gap={3}>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            colorScheme={action === "burn" ? "red" : "purple"}
            isDisabled={!canExecute || (action === "send" && !isAddress(targetAddress))}
            isLoading={state.status === "executing"}
            onClick={execute}
          >
            Confirm
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function PositionRow({ position, onAction, isSelected, onToggleSelected }) {
  return (
    <Tr _hover={{ bg: "whiteAlpha.100" }}>
      <Td>
        <Checkbox isChecked={isSelected} onChange={() => onToggleSelected(position.id)} />
      </Td>
      <Td color="whiteAlpha.800" fontFamily="mono">{formatAddress(position.walletAddress)}</Td>
      <Td>
        <Badge colorScheme={position.chainId === 42161 ? "cyan" : "orange"}>{position.chainName || position.chainId}</Badge>
      </Td>
      <Td>
        <HStack>
          {position.imageUrl ? (
            <Image src={position.imageUrl} alt={position.symbol} boxSize="32px" borderRadius="full" />
          ) : (
            <Box boxSize="32px" borderRadius="full" bg="whiteAlpha.200" display="grid" placeItems="center">
              <Text fontSize="sm" fontWeight="bold">{String(position.symbol || "?").slice(0, 2)}</Text>
            </Box>
          )}
          <Box>
            <Text fontWeight="semibold" color="white">{position.symbol}</Text>
            <Text fontSize="xs" color="whiteAlpha.600">{position.name}</Text>
          </Box>
        </HStack>
      </Td>
      <Td><Badge variant="subtle">{position.assetType}</Badge></Td>
      <Td color="whiteAlpha.900">{formatBigIntAmount(position.balance, position.decimals)} {position.symbol}</Td>
      <Td color="green.200">{formatCurrency(position.estimatedValueUsd, "USD")}</Td>
      <Td>
        <Badge colorScheme={position.balance > 0n ? "green" : "gray"}>
          {position.balance > 0n ? "Ready" : "Empty"}
        </Badge>
      </Td>
      <Td>
        <HStack>
          <Button size="sm" colorScheme="cyan" isDisabled={!position.canSell} onClick={() => onAction("sell", position)}>
            Sell all
          </Button>
          {position.marketplaceUrl ? (
            <Button
              as={CLink}
              size="sm"
              colorScheme="purple"
              href={position.marketplaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              _hover={{ textDecoration: "none" }}
            >
              Sell NFT
            </Button>
          ) : null}
          <Button size="sm" colorScheme="blue" variant="outline" borderColor="blue.300" onClick={() => onAction("send", position)}>
            Send
          </Button>
          <Button size="sm" colorScheme="red" variant="outline" borderColor="red.300" isDisabled={!position.canBurn} onClick={() => onAction("burn", position)}>
            Burn all
          </Button>
        </HStack>
      </Td>
    </Tr>
  );
}

function PositionsTable({ positions, onAction, selectedIds, onToggleSelected, onToggleAll }) {
  if (!positions.length) return <EmptyState>No data available.</EmptyState>;
  const allSelected = positions.length > 0 && positions.every((position) => selectedIds.has(position.id));
  return (
    <Box overflowX="auto" borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" bg="blackAlpha.300">
      <Table size="sm">
        <Thead bg="whiteAlpha.100">
          <Tr>
            <Th color="whiteAlpha.600">
              <Checkbox isChecked={allSelected} onChange={() => onToggleAll(positions)} />
            </Th>
            <Th color="whiteAlpha.600">Wallet</Th>
            <Th color="whiteAlpha.600">Chain</Th>
            <Th color="whiteAlpha.600">Asset</Th>
            <Th color="whiteAlpha.600">Type</Th>
            <Th color="whiteAlpha.600">Balance</Th>
            <Th color="whiteAlpha.600">Estimated value</Th>
            <Th color="whiteAlpha.600">Status</Th>
            <Th color="whiteAlpha.600">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {positions.map((position) => (
            <PositionRow
              key={position.id}
              position={position}
              onAction={onAction}
              isSelected={selectedIds.has(position.id)}
              onToggleSelected={onToggleSelected}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function BuybackPlanRow({ plan, onBuyBack }) {
  const status = getBuybackPlanStatus(plan);
  return (
    <Tr _hover={{ bg: "whiteAlpha.100" }}>
      <Td color="white">{plan.symbol}</Td>
      <Td color="whiteAlpha.800" fontFamily="mono">{formatAddress(plan.walletAddress)}</Td>
      <Td><Badge colorScheme={plan.chainId === 42161 ? "cyan" : "orange"}>{plan.chainName || plan.chainId}</Badge></Td>
      <Td color="whiteAlpha.800">{plan.amountSold}</Td>
      <Td color="whiteAlpha.700">{formatDateTime(plan.soldAt)}</Td>
      <Td color="whiteAlpha.700">{formatDateTime(plan.availableFrom)}</Td>
      <Td><Badge colorScheme={status === "available" ? "green" : status === "completed" ? "blue" : "gray"}>{status}</Badge></Td>
      <Td>
        <Button size="sm" colorScheme="green" isDisabled={!isBuybackAvailable({ ...plan, status })} onClick={() => onBuyBack(plan)}>
          Buy back
        </Button>
      </Td>
    </Tr>
  );
}

function BuybackPlansTable({ plans, onBuyBack }) {
  if (!plans.length) return <EmptyState>No buyback plans yet.</EmptyState>;
  return (
    <Box overflowX="auto" borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" bg="blackAlpha.300">
      <Table size="sm">
        <Thead bg="whiteAlpha.100">
          <Tr>
            <Th color="whiteAlpha.600">Asset</Th>
            <Th color="whiteAlpha.600">Wallet</Th>
            <Th color="whiteAlpha.600">Chain</Th>
            <Th color="whiteAlpha.600">Amount sold</Th>
            <Th color="whiteAlpha.600">Sold at</Th>
            <Th color="whiteAlpha.600">Available from</Th>
            <Th color="whiteAlpha.600">Status</Th>
            <Th color="whiteAlpha.600">Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {plans.map((plan) => (
            <BuybackPlanRow key={plan.id} plan={plan} onBuyBack={onBuyBack} />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function MetricTile({ label, value, detail, color = "cyan.200" }) {
  return (
    <Box borderWidth="1px" borderColor="whiteAlpha.200" bg="whiteAlpha.100" borderRadius="md" p={4}>
      <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase">{label}</Text>
      <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
      {detail ? <Text fontSize="xs" color="whiteAlpha.600">{detail}</Text> : null}
    </Box>
  );
}

export default function AssetExecutionDashboard() {
  const { account, user } = useMoralis();
  const userId = user?.id || account || "local-user";
  const store = useAssetExecutionStore();
  const providers = useSellProviders();
  const { positions, loading, error, refresh } = useWalletPositions(account);
  const { plans, refresh: refreshPlans } = useBuybackPlans(store, userId);
  const drawer = useDisclosure();
  const [walletFilter, setWalletFilter] = useState("all");
  const [chainFilter, setChainFilter] = useState("all");
  const [assetView, setAssetView] = useState("tokens");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedBatchPositions, setSelectedBatchPositions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const wallets = useMemo(() => Array.from(new Set(positions.map((position) => position.walletAddress))), [positions]);
  const chains = useMemo(() => Array.from(new Set(positions.map((position) => position.chainId))), [positions]);
  const totalValueUsd = positions.reduce((total, position) => total + (position.estimatedValueUsd || 0), 0);
  const executableCount = positions.filter((position) => position.canSell || position.canBurn).length;
  const tokenCount = positions.filter((position) => position.assetType === "native" || position.assetType === "erc20").length;
  const nftCount = positions.filter((position) => position.assetType === "erc721" || position.assetType === "erc1155").length;
  const hasLiveData = positions.length > 0;
  const filteredPositions = positions.filter((position) => {
    const walletOk = walletFilter === "all" || position.walletAddress === walletFilter;
    const chainOk = chainFilter === "all" || String(position.chainId) === chainFilter;
    const typeOk = assetView === "tokens"
      ? position.assetType === "native" || position.assetType === "erc20"
      : position.assetType === "erc721" || position.assetType === "erc1155";
    return walletOk && chainOk && typeOk;
  });
  const selectedPositions = filteredPositions.filter((position) => selectedIds.has(position.id));

  const openAction = (action, position) => {
    setSelectedAction(action);
    setSelectedPosition(position);
    setSelectedBatchPositions([]);
    setSelectedPlan(null);
    drawer.onOpen();
  };

  const openBatchAction = (action) => {
    setSelectedAction(action);
    setSelectedPosition(null);
    setSelectedBatchPositions(selectedPositions);
    setSelectedPlan(null);
    drawer.onOpen();
  };

  const openBuyBack = (plan) => {
    setSelectedAction("buyBack");
    setSelectedPosition(null);
    setSelectedBatchPositions([]);
    setSelectedPlan(plan);
    drawer.onOpen();
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (rows) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = rows.every((position) => next.has(position.id));
      rows.forEach((position) => {
        if (allSelected) next.delete(position.id);
        else next.add(position.id);
      });
      return next;
    });
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [assetView, walletFilter, chainFilter]);

  const refreshAll = async () => {
    await Promise.all([refresh(), refreshPlans()]);
  };

  return (
    <Box p={{ base: 4, md: 8 }} bg="linear-gradient(135deg, #070b14 0%, #101723 55%, #111827 100%)" color="white" minH="100vh">
      <VStack align="stretch" spacing={7} maxW="1500px" mx="auto">
        <Flex justify="space-between" align={{ base: "stretch", md: "flex-start" }} gap={5} direction={{ base: "column", lg: "row" }}>
          <Box>
            <HStack spacing={3} mb={2}>
              <Badge colorScheme={hasLiveData ? "green" : "gray"} px={2} py={1}>
                {hasLiveData ? "Live wallet data" : "No data available"}
              </Badge>
              <Badge colorScheme="cyan" px={2} py={1}>Moralis + ethers</Badge>
            </HStack>
            <Text fontSize={{ base: "2xl", md: "4xl" }} fontWeight="bold">Asset Execution</Text>
            <Text color="whiteAlpha.700">Burn, send, sell, and schedule buybacks across saved wallets.</Text>
          </Box>
          <HStack flexWrap="wrap" justify={{ base: "flex-start", lg: "flex-end" }}>
            <Select value={walletFilter} onChange={(event) => setWalletFilter(event.target.value)} maxW="220px" bg="blackAlpha.500" borderColor="whiteAlpha.300">
              <option value="all">All wallets</option>
              {wallets.map((wallet) => (
                <option key={wallet} value={wallet}>{formatAddress(wallet)}</option>
              ))}
            </Select>
            <Select value={chainFilter} onChange={(event) => setChainFilter(event.target.value)} maxW="180px" bg="blackAlpha.500" borderColor="whiteAlpha.300">
              <option value="all">All chains</option>
              {chains.map((chainId) => (
                <option key={chainId} value={chainId}>{positions.find((p) => p.chainId === chainId)?.chainName || chainId}</option>
              ))}
            </Select>
            <Button onClick={refreshAll} isLoading={loading} colorScheme="cyan">Refresh</Button>
          </HStack>
        </Flex>

        <Flex gap={4} direction={{ base: "column", md: "row" }}>
          <Box flex="1"><MetricTile label="Wallets" value={wallets.length} detail="Connected plus saved wallets" /></Box>
          <Box flex="1"><MetricTile label="Positions" value={positions.length} detail={`${executableCount} executable rows`} color="green.200" /></Box>
          <Box flex="1"><MetricTile label="Estimated value" value={formatCurrency(totalValueUsd, "USD")} detail="Moralis priced rows only" color="yellow.200" /></Box>
          <Box flex="1"><MetricTile label="Buyback plans" value={plans.length} detail={`${plans.filter((plan) => isBuybackAvailable(plan)).length} available`} color="purple.200" /></Box>
        </Flex>

        {error ? <ErrorState message={error} /> : null}

        <Box>
          <Flex justify="space-between" align={{ base: "stretch", md: "center" }} mb={3} gap={3} direction={{ base: "column", md: "row" }}>
            <Box>
              <Text fontSize="xl" fontWeight="bold">{assetView === "tokens" ? "Token positions" : "NFT positions"}</Text>
              <Text fontSize="sm" color="whiteAlpha.600">Rows stay separated by wallet and asset for one-transaction execution.</Text>
            </Box>
            <HStack flexWrap="wrap">
              <HStack spacing={1} bg="blackAlpha.400" borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" p={1}>
                <Button size="sm" colorScheme={assetView === "tokens" ? "cyan" : "gray"} variant={assetView === "tokens" ? "solid" : "ghost"} onClick={() => setAssetView("tokens")}>
                  Tokens ({tokenCount})
                </Button>
                <Button size="sm" colorScheme={assetView === "nfts" ? "cyan" : "gray"} variant={assetView === "nfts" ? "solid" : "ghost"} onClick={() => setAssetView("nfts")}>
                  NFTs ({nftCount})
                </Button>
              </HStack>
              <Button size="sm" colorScheme="cyan" isDisabled={!selectedPositions.length || assetView !== "tokens" || selectedPositions.some((position) => !position.canSell)} onClick={() => openBatchAction("sell")}>
                Sell selected
              </Button>
              <Button size="sm" colorScheme="blue" variant="outline" isDisabled={!selectedPositions.length} onClick={() => openBatchAction("send")}>
                Send selected
              </Button>
              <Button size="sm" colorScheme="red" variant="outline" isDisabled={!selectedPositions.length || selectedPositions.some((position) => !position.canBurn)} onClick={() => openBatchAction("burn")}>
                Burn selected
              </Button>
            </HStack>
          </Flex>
          {loading ? (
            <LoadingState />
          ) : (
            <PositionsTable
              positions={filteredPositions}
              onAction={openAction}
              selectedIds={selectedIds}
              onToggleSelected={toggleSelected}
              onToggleAll={toggleAll}
            />
          )}
        </Box>

        <Divider borderColor="whiteAlpha.200" />

        <Box>
          <Text fontSize="xl" fontWeight="bold" mb={1}>Buyback plans</Text>
          <Text fontSize="sm" color="whiteAlpha.600" mb={3}>Plans unlock automatically on the first day of the next calendar year.</Text>
          <BuybackPlansTable plans={plans} onBuyBack={openBuyBack} />
        </Box>
      </VStack>

      <ExecutionDrawer
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
        action={selectedAction}
        position={selectedPosition}
        batchPositions={selectedBatchPositions}
        plan={selectedPlan}
        providers={providers}
        account={account}
        store={store}
        userId={userId}
        onChanged={refreshAll}
      />
    </Box>
  );
}
